import * as React from "react";
import { PriceChange } from "./PriceChange";
import { MarketSummary } from "swan-public-api-client";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import { connect } from "react-redux";
import { AppState } from "../Store/RootReducer";
import {
  ColumnVisibility,
  ColumnDefinition,
  ColumnsProvider,
  GridColumnsProvider
} from "../Grid/Columns";
import "./MarketsGrid.less";

export interface NamedColumnDefinition extends ColumnDefinition {
  label: string;
}

export function getDefaultMarketColumns(): NamedColumnDefinition[] {
  const columns = [
    {
      key: "name",
      visibility: ColumnVisibility.Visible,
      label: "Market Name"
    },
    {
      key: "bid",
      visibility: ColumnVisibility.Visible,
      label: "Bid"
    },
    {
      key: "offer",
      visibility: ColumnVisibility.Visible,
      label: "Offer"
    },
    {
      key: "cod",
      visibility: ColumnVisibility.Visible,
      label: "Change on Day (%)"
    },
    {
      key: "low",
      visibility: ColumnVisibility.Hidden,
      label: "Low"
    },
    {
      key: "high",
      visibility: ColumnVisibility.Hidden,
      label: "High"
    }
  ];
  return columns;
}

interface Props extends WrappedComponentProps {
  markets: Array<PriceChange<MarketSummary>>;
  columnsProvider: ColumnsProvider<ColumnProps<PriceChange<MarketSummary>>>;
}

// can move to separate files as column and renderer function definitions
const priceFlashRenderer = (
  text: string,
  record: PriceChange<MarketSummary>
) => {
  return <span className={"grid-price-" + record.direction}>{text}</span>;
};
const posnegPercentageRenderer = (
  selector: (x: PriceChange<MarketSummary>) => number
) => (text: string, record: PriceChange<MarketSummary>, index: number) => {
  const value = selector(record);
  let className = "grid-";
  if (value > 0) {
    className += "positive";
  } else if (value < 0) {
    className += "negative";
  } else {
    className = "";
  }
  return <span className={className}>{text}%</span>;
};

// tslint:disable-next-line:max-classes-per-file
const BasicMarketsGrid = (props: Props) => {
  const { markets, columnsProvider } = props;
  const offer = props.intl.formatMessage({ id: "grid.offer" });
  const bid = props.intl.formatMessage({ id: "grid.bid" });
  const cod = props.intl.formatMessage({ id: "grid.cod" });
  const name = props.intl.formatMessage({ id: "grid.marketName" });
  const low = props.intl.formatMessage({ id: "grid.low" });
  const high = props.intl.formatMessage({ id: "grid.high" });
  const columns: Array<ColumnProps<PriceChange<MarketSummary>>> = [
    {
      key: "name",
      dataIndex: "data.Name",
      width: 2,
      title: name,
      sorter: (a, b) => (a.data.Name > b.data.Name ? 1 : -1),
      defaultSortOrder: "ascend"
    },
    {
      key: "bid",
      dataIndex: "widenedPrice.bid",
      width: 1,
      title: bid,
      render: priceFlashRenderer
    },
    {
      key: "offer",
      dataIndex: "widenedPrice.ask",
      width: 1,
      title: offer,
      render: priceFlashRenderer
    },
    {
      key: "cod",
      dataIndex: "data.Price.ChangeOnDayPercentage",
      width: 1,
      title: cod,
      sorter: (a, b) => {
        return a.data.Price.ChangeOnDayPercentage >
          b.data.Price.ChangeOnDayPercentage
          ? 1
          : -1;
      },
      render: posnegPercentageRenderer(x =>
        parseFloat(x.data.Price.ChangeOnDayPercentage.toString())
      )
    },
    {
      key: "low",
      // todo: the low and the high don't have spread widening applied so
      // it's possible the user will see (and possibly trade!) a price that goes higher than the high and lower than the low
      dataIndex: "data.Price.Low",
      width: 1,
      title: low
    },
    {
      key: "high",
      dataIndex: "data.Price.High",
      width: 1,
      title: high
    }
  ];
  const rowKey = (x: PriceChange<MarketSummary>) => x.data.Key;
  const pickedColumns = columnsProvider.getPickedColumns(columns);
  return (
    <Table
      dataSource={markets}
      columns={pickedColumns}
      pagination={{ pageSize: 20 }}
      size={"middle"}
      rowKey={rowKey}
    />
  );
};

const MarketsGridWithIntl = injectIntl(BasicMarketsGrid);

const mapStateToProps = (state: AppState) => ({
  columnsProvider: new GridColumnsProvider<PriceChange<MarketSummary>>(
    (state.settingsMarketsGrid && state.settingsMarketsGrid.columns) ||
      getDefaultMarketColumns()
  )
});

export const MarketsGrid = connect(mapStateToProps)(MarketsGridWithIntl);
